import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';

import type { PlayerStore } from '../../state/player_store.js';

const logger = DebugLogger.getInstance('unified_player_media_event_handler');
const drmLogger = DebugLogger.getInstance('unified_player_drm');

const PLAYBACK_CHANGE_EVENTS = ['pause', 'waiting', 'playing', 'ended'];

const TRUNCATED_MEDIA_TOLERANCE_SECONDS = 1;

type PlayIntentionHandler = (shouldBePlaying: boolean) => void;

export class MediaEventHandler {
  #mediaElement: HTMLMediaElement;

  #store: PlayerStore;

  #playIntentionHandler: PlayIntentionHandler;

  constructor(
    mediaElement: HTMLMediaElement,
    store: PlayerStore,
    playIntentionHandler: PlayIntentionHandler,
  ) {
    this.#mediaElement = mediaElement;
    this.#store = store;
    this.#playIntentionHandler = playIntentionHandler;
  }

  init(): void {
    this.#mediaElement.addEventListener('loadstart', this.setLoading);
    this.#mediaElement.addEventListener('canplaythrough', this.setLoaded);
    this.#mediaElement.addEventListener('waiting', this.setLoading);

    this.#mediaElement.addEventListener('canplaythrough', this.#handleCanPlay);
    this.#mediaElement.addEventListener(
      'durationchange',
      this.#handleDurationChange,
    );
    this.#mediaElement.addEventListener(
      'loadedmetadata',
      this.#handleLoadedMetadata,
    );
    PLAYBACK_CHANGE_EVENTS.forEach((event) => {
      this.#mediaElement.addEventListener(event, this.#handlePlaybackChange);
    });

    this.#mediaElement.addEventListener('seeking', this.#handleSeeking);
    this.#mediaElement.addEventListener('seeked', this.#handleSeeked);

    this.#mediaElement.addEventListener('error', this.#handleError);

    // EME events for DRM debugging
    this.#mediaElement.addEventListener('encrypted', this.#handleEncrypted);
    this.#mediaElement.addEventListener(
      'waitingforkey',
      this.#handleWaitingForKey,
    );
  }

  destroy(): void {
    this.#mediaElement.removeEventListener('loadstart', this.setLoading);
    this.#mediaElement.removeEventListener('canplaythrough', this.setLoaded);
    this.#mediaElement.removeEventListener('waiting', this.setLoading);

    this.#mediaElement.removeEventListener(
      'canplaythrough',
      this.#handleCanPlay,
    );
    this.#mediaElement.removeEventListener(
      'durationchange',
      this.#handleDurationChange,
    );
    this.#mediaElement.removeEventListener(
      'loadedmetadata',
      this.#handleLoadedMetadata,
    );
    PLAYBACK_CHANGE_EVENTS.forEach((event) => {
      this.#mediaElement.removeEventListener(event, this.#handlePlaybackChange);
    });

    this.#mediaElement.removeEventListener('seeking', this.#handleSeeking);
    this.#mediaElement.removeEventListener('seeked', this.#handleSeeked);

    this.#mediaElement.removeEventListener('error', this.#handleError);

    // remove EME listeners
    this.#mediaElement.removeEventListener('encrypted', this.#handleEncrypted);
    this.#mediaElement.removeEventListener(
      'waitingforkey',
      this.#handleWaitingForKey,
    );
  }

  #handlePlaybackChange = (event: Event) => {
    if (this.#store.playableMedia.$playbackRate.get() < 0) {
      return;
    }
    const shouldLoop = this.#store.playableMedia.$shouldLoop.get();

    if (event.type === 'ended' && !shouldLoop) {
      this.#store.playableMedia.setLastFoundFrameTime(
        this.#mediaElement.currentTime,
      );
    }
    const shouldBePlaying = this.#store.playableMedia.$shouldBePlaying.get();
    this.#store.playableMedia.setPlaying(!this.#mediaElement.paused);
    // Can't look at both the ended property and listen for the ended event because of
    // different event timings in different browsers. The property is the most consistent.
    if (this.#mediaElement.ended) {
      // Checking shouldBePlaying because we get ended event in some browsers when seeking to the last frame.
      if (shouldBePlaying && shouldLoop) {
        this.#store.handleLooping();
        return;
      }
    }
    if (event.type === 'playing' || event.type === 'pause') {
      this.#store.playableMedia.setShouldBePlaying(!this.#mediaElement.paused);
    }
  };

  #handleCanPlay = () => {
    this.#playIntentionHandler(
      this.#store.playableMedia.$shouldBePlaying.get(),
    );
  };

  #handleDurationChange = () => {
    // While live or encoding the element reports the end of what was appended,
    // not the media's length, and the stream player already owns duration there
    if (
      this.#store.playableMedia.$isLive.get() ||
      this.#store.playableMedia.$isEncoding.get()
    ) {
      return;
    }

    const mediaDuration = this.#mediaElement.duration;
    const itemDuration =
      this.#store.playlist.$currentPlaylistItem.get()?.duration;

    // A container that stopped short would otherwise shrink the timeline to
    // whatever it holds, with nothing to say the rest of the media is missing.
    // Only a real shortfall counts: a proxy is routinely a frame or two off its
    // source, and that drift is the element's to correct
    if (
      itemDuration !== undefined &&
      Number.isFinite(mediaDuration) &&
      itemDuration - mediaDuration > TRUNCATED_MEDIA_TOLERANCE_SECONDS
    ) {
      return;
    }

    const storeDuration = this.#store.playableMedia.$duration.get();

    // Always update if we get a valid duration from the media element and either:
    // 1. We don't have a duration in the store yet
    // 2. The difference is significant (more than 0.1 seconds)
    if (
      Number.isFinite(mediaDuration) &&
      mediaDuration > 0 &&
      (storeDuration === undefined ||
        Math.abs(storeDuration - mediaDuration) > 0.1)
    ) {
      this.#store.playableMedia.setDuration(mediaDuration);
    }
  };

  #handleLoadedMetadata = () => {
    if (this.#mediaElement instanceof HTMLVideoElement) {
      this.#store.size.setNaturalSize({
        width: this.#mediaElement.videoWidth,
        height: this.#mediaElement.videoHeight,
      });
    }
  };

  #handleEncrypted = (event: MediaEncryptedEvent) => {
    drmLogger.debug('Media encrypted', {
      initDataType: event.initDataType,
      initDataBytes: event.initData?.byteLength ?? 0,
    });
  };

  #handleWaitingForKey = () => {
    drmLogger.debug(
      'waitingforkey: browser has encrypted segments but no usable key in CDM',
    );
  };

  #handleSeeking = () => {
    this.#store.playableMedia.setSeeking(true);
    const playableRange = this.#store.playableMedia.$playableRange.get();
    if (this.#mediaElement.currentTime < playableRange.start) {
      this.#mediaElement.currentTime = playableRange.start;
    }
    if (this.#mediaElement.currentTime > playableRange.end) {
      this.#store.playableMedia.pause();
      this.#mediaElement.currentTime = playableRange.end;
    }
  };

  #handleSeeked = () => {
    this.#store.playableMedia.setSeeking(false);
    if (
      this.#store.playableMedia.$shouldBePlaying.get() &&
      this.#mediaElement.paused
    ) {
      this.#mediaElement.play();
    }
  };

  setLoading = () => {
    this.#store.setLoadState('loading');
  };

  setLoaded = () => {
    this.#store.setLoadState('loaded');
  };

  #handleError = () => {
    logger.debug(
      '[MediaEventHandler] media element error',
      this.#mediaElement.error,
    );
    this.#store.setLoadState('error');
  };
}
